import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GeolocationContext, type GeolocationContextProps } from '../contexts/geolocation.context';

import { getGeolocation } from '../utils/get-geolocation';

interface GeolocationProviderProps {
  children: ReactNode;
  enabled?: boolean;
}

export function GeolocationProvider({ children, enabled = true }: GeolocationProviderProps) {
  const geolocationQuery = useQuery({
    queryKey: ['geolocation'],
    queryFn: async () => {
      const position = await getGeolocation();
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
    },
    meta: {
      persist: true,
    },
    enabled: enabled,
    staleTime: 15 * 60 * 1000, // 15 minutos
    gcTime: 60 * 60 * 1000, // 1 hora
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000), // Max 10s
    retry: (failureCount, error) => {
      // Só retry para erros de rede, não para permissão negada
      if (error.message?.includes('network') || error.name === 'TypeError') {
        return failureCount < 1; // Apenas 1 retry
      }
      return false;
    },
  });

  const ctx: GeolocationContextProps = {
    coordinates: geolocationQuery.data || null,
    isLoading: geolocationQuery.isLoading,
  };

  return <GeolocationContext.Provider value={ctx}>{children}</GeolocationContext.Provider>;
}
